import { useState, useEffect, useRef } from 'react';

interface UseGridLayoutProps {
  onCardLayoutChange?: (width: number, gap: number) => void;
}

export function useGridLayout({ onCardLayoutChange }: UseGridLayoutProps) {
  const [gridGap, setGridGap] = useState(16);
  const [numColumns, setNumColumns] = useState(3);
  const [cardWidth, setCardWidth] = useState(288);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calculateGridLayout = () => {
      if (!gridContainerRef.current) return;
      
      const container = gridContainerRef.current.parentElement;
      if (!container) return;
      
      const containerWidth = container.offsetWidth;
      const padding = 16; // Padding on each side
      const fixedGap = 16; // Fixed gap both horizontal and vertical
      
      // Calculate horizontal layout
      const availableWidth = containerWidth - (padding * 2);
      
      // Start with minimum number of columns and increase until cards become too small
      const minCardWidth = 250; // Minimum card width
      let columns = Math.max(1, Math.floor((availableWidth + fixedGap) / (minCardWidth + fixedGap)));
      columns = Math.min(3, columns);

      // Calculate card width to fill the space with fixed gaps
      const totalGapSpace = (columns - 1) * fixedGap;
      const calculatedCardWidth = (availableWidth - totalGapSpace) / columns;
      
      const finalCardWidth = Math.max(minCardWidth, calculatedCardWidth);
      
      setNumColumns(columns);
      setCardWidth(finalCardWidth);
      setGridGap(fixedGap);

      // Call the onCardLayoutChange callback if provided
      if (onCardLayoutChange) {
        onCardLayoutChange(finalCardWidth, fixedGap);
      }
    };

    calculateGridLayout();
    window.addEventListener('resize', calculateGridLayout);
    return () => window.removeEventListener('resize', calculateGridLayout);
  }, [onCardLayoutChange]);

  return {
    gridGap,
    numColumns,
    cardWidth,
    gridContainerRef
  };
}
